import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import dfId from "date-fns/locale/id";
import dfEnGB from "date-fns/locale/en-GB";

import en from "./en";
import id from "./id";
import flag_GBR from "../assets/images/flags/GBR.png";
import flag_IDN from "../assets/images/flags/IDN.png";

const DICTS = { en, id };

export const LANGUAGES = [
  { code: "en", name: "English", short: "EN", flag: flag_GBR, intl: "en-GB" },
  { code: "id", name: "Bahasa Indonesia", short: "ID", flag: flag_IDN, intl: "id-ID" },
];

registerLocale("en", dfEnGB);
registerLocale("id", dfId);

function initialLanguage() {
  try {
    const saved = localStorage.getItem("language");
    if (DICTS[saved]) return saved;
  } catch {
    // Storage blocked; fall through to the browser's language.
  }
  return (navigator.language || "").toLowerCase().startsWith("id") ? "id" : "en";
}

// The language of the current render. Module-level so plain helpers (date
// formatting, toast messages) can follow it without threading it through.
let current = initialLanguage();

export const currentLanguage = () => current;
export const intlLocale = () => (LANGUAGES.find((l) => l.code === current) || LANGUAGES[0]).intl;

/**
 * A dictionary entry is a string with {name} placeholders, or a function of the
 * values -- for counts, and for sentences that wrap a value in markup.
 * A key missing from Indonesian falls back to English rather than showing the key.
 */
export function translate(lang, key, vars) {
  const entry = DICTS[lang]?.[key] ?? DICTS.en[key];
  if (entry === undefined) return key;
  if (typeof entry === "function") return entry(vars || {});
  if (!vars) return entry;
  return entry.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m));
}

export const t = (key, vars) => translate(current, key, vars);

const I18nContext = createContext({ lang: current, setLang: () => {}, t });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(current);
  current = lang;

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(() => ({
    lang,
    setLang: (code) => {
      if (!DICTS[code]) return;
      try { localStorage.setItem("language", code); } catch { /* not persisted */ }
      current = code;
      setLangState(code);
    },
    t: (key, vars) => translate(lang, key, vars),
  }), [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);

/** For the class components. */
export function withI18n(Component) {
  return function WithI18n(props) {
    const i18n = useI18n();
    return <Component {...props} i18n={i18n} />;
  };
}
